import React, { useEffect, useState } from 'react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Shield, ShieldCheck, Copy, Check, Loader2 } from 'lucide-react';

const Admin2FA = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [enableCode, setEnableCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const loadStatus = async () => {
    try {
      const res = await api.get('/api/admin/2fa/status');
      setStatus(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await api.post('/api/admin/2fa/setup');
      setSetupData(res.data);
      toast.success('Scan the QR code with Google Authenticator or Authy');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  const enable2FA = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/admin/2fa/enable', { code: enableCode });
      toast.success('2FA enabled! You will be prompted next time you sign in.');
      setSetupData(null);
      setEnableCode('');
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    if (!window.confirm('Disable two-factor authentication for the admin account? You can re-enable it any time.')) return;
    setBusy(true);
    try {
      await api.post('/api/admin/2fa/disable', { code: disableCode });
      toast.success('2FA disabled');
      setDisableCode('');
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = (code, idx) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-lime-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-6 md:p-10" data-testid="admin-2fa-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          {status?.enabled ? (
            <ShieldCheck className="w-8 h-8 text-lime-400" />
          ) : (
            <Shield className="w-8 h-8 text-zinc-400" />
          )}
          <div>
            <h1 className="text-3xl font-bold">Two-Factor Authentication</h1>
            <p className="text-zinc-400">Admin account protection</p>
          </div>
        </div>

        <div className={`rounded-xl border p-6 mb-6 ${status?.enabled ? 'border-lime-500/40 bg-lime-500/5' : 'border-zinc-800 bg-zinc-900'}`} data-testid="admin-2fa-status">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-zinc-400">Status</p>
              <p className="text-2xl font-bold">{status?.enabled ? 'Enabled' : 'Disabled'}</p>
            </div>
            {status?.enabled && (
              <div className="text-right">
                <p className="text-sm text-zinc-400">Recovery codes</p>
                <p className="text-2xl font-bold text-lime-400">{status.recovery_codes_remaining} left</p>
              </div>
            )}
          </div>
        </div>

        {/* --- ENABLE FLOW --- */}
        {!status?.enabled && !setupData && (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 space-y-4">
            <h2 className="text-xl font-semibold">Enable 2FA</h2>
            <p className="text-zinc-400 text-sm">
              You'll need an authenticator app (Google Authenticator, Authy, 1Password, etc.) on your phone. After enabling, every admin login will require a 6-digit code from your app.
            </p>
            <button
              onClick={startSetup}
              disabled={busy}
              data-testid="admin-2fa-start-setup-btn"
              className="px-6 py-3 bg-gradient-to-r from-lime-500 to-lime-600 text-black font-bold rounded-lg hover:from-lime-600 hover:to-lime-700 disabled:opacity-50"
            >
              {busy ? 'Generating…' : 'Start Setup'}
            </button>
          </div>
        )}

        {!status?.enabled && setupData && (
          <div className="rounded-xl border border-lime-500/40 bg-zinc-900 p-6 space-y-6" data-testid="admin-2fa-setup-panel">
            <div>
              <h2 className="text-xl font-semibold mb-2">Step 1 — Scan with your authenticator</h2>
              <div className="bg-white p-4 rounded-lg inline-block">
                <img src={setupData.qr_code_data_url} alt="2FA QR Code" className="block" width="220" height="220" />
              </div>
              <p className="text-sm text-zinc-400 mt-3">
                Can't scan? Enter this secret manually:
              </p>
              <code className="block mt-1 p-3 bg-black border border-zinc-700 rounded font-mono text-lime-300 break-all">
                {setupData.secret}
              </code>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-2">Step 2 — Save your recovery codes</h2>
              <p className="text-sm text-zinc-400 mb-3">
                Write these down or save them somewhere safe. If you lose your phone, each code can be used <strong>once</strong> to sign in.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {setupData.recovery_codes.map((code, i) => (
                  <button
                    key={i}
                    onClick={() => copyCode(code, i)}
                    className="flex items-center justify-between p-3 bg-black border border-zinc-700 rounded font-mono text-sm hover:border-lime-500 transition"
                    data-testid={`admin-2fa-recovery-code-${i}`}
                  >
                    <span>{code}</span>
                    {copiedIndex === i ? <Check className="w-4 h-4 text-lime-400" /> : <Copy className="w-4 h-4 text-zinc-500" />}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={enable2FA} className="space-y-3">
              <h2 className="text-xl font-semibold">Step 3 — Confirm the 6-digit code</h2>
              <input
                type="text"
                placeholder="123456"
                value={enableCode}
                onChange={(e) => setEnableCode(e.target.value)}
                required
                data-testid="admin-2fa-enable-code-input"
                className="w-full p-3 bg-black border border-zinc-700 rounded text-center tracking-widest text-xl font-mono"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="admin-2fa-enable-btn"
                className="w-full p-3 bg-gradient-to-r from-lime-500 to-lime-600 text-black font-bold rounded-lg hover:from-lime-600 hover:to-lime-700 disabled:opacity-50"
              >
                {busy ? 'Verifying…' : 'Enable 2FA'}
              </button>
            </form>
          </div>
        )}

        {/* --- DISABLE FLOW --- */}
        {status?.enabled && (
          <form onSubmit={disable2FA} className="rounded-xl border border-red-500/30 bg-zinc-900 p-6 space-y-4">
            <h2 className="text-xl font-semibold">Disable 2FA</h2>
            <p className="text-sm text-zinc-400">Enter a current 6-digit code (or a recovery code) to turn off two-factor authentication.</p>
            <input
              type="text"
              placeholder="123456"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              required
              data-testid="admin-2fa-disable-code-input"
              className="w-full p-3 bg-black border border-zinc-700 rounded text-center tracking-widest text-xl font-mono"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-2fa-disable-btn"
              className="w-full p-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {busy ? 'Verifying…' : 'Disable 2FA'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Admin2FA;
